import { getAnalytics, logEvent, setUserProperties, setUserId } from 'firebase/analytics';
import type { Analytics } from 'firebase/analytics';
import { app } from './app';

let analytics: Analytics | null = null;

// Initialize Firebase Analytics
export const initializeAnalytics = (): Analytics | null => {
  try {
    // Only initialize in browser environment
    if (typeof window !== 'undefined') {
      analytics = getAnalytics(app);
      console.log('🔍 Firebase Analytics initialized');
      
      // Log a simple test event to confirm it's working
      logEvent(analytics, 'app_initialized', {
        timestamp: new Date().toISOString(),
        app_version: '1.0.0',
        platform: 'web'
      });
      
      return analytics;
    }
    return null;
  } catch (error) {
    console.error('Failed to initialize Firebase Analytics:', error);
    return null;
  }
};

// Get analytics instance
export const getAnalyticsInstance = (): Analytics | null => {
  if (!analytics && typeof window !== 'undefined') {
    analytics = initializeAnalytics();
  }
  return analytics;
};

// Enhanced event logging functions
export const logUserEvent = (eventName: string, parameters?: Record<string, any>) => {
  const analyticsInstance = getAnalyticsInstance();
  if (analyticsInstance) {
    logEvent(analyticsInstance, eventName, {
      timestamp: new Date().toISOString(),
      ...parameters
    });
  }
};

// Authentication events
export const logSignIn = (method: string) => {
  logUserEvent('login', { method });
};

export const logSignOut = () => {
  logUserEvent('logout');
};

// License management events
export const logLicenseCreated = (licenseType?: string) => {
  logUserEvent('license_created', { license_type: licenseType });
};

export const logLicenseDeleted = () => {
  logUserEvent('license_deleted');
};

export const logLicenseKeyCopied = () => {
  logUserEvent('license_key_copied');
};

// Page view tracking
export const logPageView = (pageName: string) => {
  logUserEvent('page_view', { page_name: pageName });
};

// Set user properties
export const setAnalyticsUserId = (userId: string) => {
  const analyticsInstance = getAnalyticsInstance();
  if (analyticsInstance) {
    setUserId(analyticsInstance, userId);
  }
};

export const setAnalyticsUserProperties = (properties: Record<string, string>) => {
  const analyticsInstance = getAnalyticsInstance();
  if (analyticsInstance) {
    setUserProperties(analyticsInstance, properties);
  }
};

// Additional analytics functions for existing code compatibility
export const trackLicensePurchase = (licenseKey: string, planName: string, amount: number, gstNumber: string) => {
  logUserEvent('purchase', {
    currency: 'INR',
    value: amount,
    item_name: planName,
    license_key: licenseKey,
    gst_number: gstNumber,
    category: 'license'
  });
};

export const trackFeatureUsage = (feature: string, action: string, metadata?: Record<string, any>) => {
  logUserEvent('feature_usage', {
    feature,
    action,
    ...metadata
  });
};

export const trackError = (errorType: string, errorMessage: string, component: string) => {
  logUserEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    component,
    severity: 'error'
  });
};

export const trackFormSubmission = (formName: string, success: boolean, errorMessage?: string) => {
  logUserEvent('form_submission', {
    form_name: formName,
    success,
    ...(errorMessage && { error_message: errorMessage })
  });
};

// Navigation and page tracking
export const trackPageView = (pageName: string) => {
  logPageView(pageName);
};

export const trackNavigation = (from: string, to: string) => {
  logUserEvent('navigation', {
    from_page: from,
    to_page: to
  });
};

// Support ticket tracking
export const trackSupportTicket = (ticketType: string, metadata?: Record<string, any>) => {
  logUserEvent('support_ticket_created', {
    ticket_type: ticketType,
    ...metadata
  });
};

export default analytics;
